import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/site/Footer";
import Logo from "@/components/site/Logo";

export default function PolitiqueConfidentialite() {
  useEffect(() => {
    document.title = "Politique de confidentialité — Solmis Habitat";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-foreground min-h-screen">
      <header className="border-b border-border bg-white/90 backdrop-blur-md sticky top-0 z-40">
        <div className="container-wide flex h-20 items-center justify-between">
          <Link to="/" data-testid="pc-logo" className="block">
            <Logo withTagline variant="dark" />
          </Link>
          <Link
            to="/"
            data-testid="pc-back"
            className="inline-flex items-center gap-2 text-sm text-primary/85 hover:text-[hsl(var(--brand-terra))] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={1.6} />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      <main className="py-20 md:py-28">
        <article className="container-narrow">
          <div className="flex items-center gap-3 mb-6">
            <span className="rule" />
            <span className="overline">Vie privée · RGPD</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-primary leading-[1.05]">
            Politique de{" "}
            <span className="italic font-light text-[hsl(var(--brand-terra))]">
              confidentialité
            </span>
          </h1>
          <p className="mt-5 text-base text-muted-foreground">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
          </p>

          <div className="mt-14 space-y-12 text-[15px] leading-relaxed text-primary/90">
            <Section index="01" title="Collecte des données">
              <p>
                Le site Solmis Habitat peut collecter les données suivantes :
              </p>
              <ul className="mt-3 space-y-1.5 list-disc pl-5 text-primary/85">
                <li>Nom</li>
                <li>Email</li>
                <li>Numéro de téléphone</li>
                <li>Informations relatives au projet</li>
              </ul>
              <p className="mt-3">
                Ces données sont collectées via les formulaires de contact.
              </p>
            </Section>

            <Section index="02" title="Utilisation des données">
              <p>
                Les données collectées sont utilisées uniquement pour :
              </p>
              <ul className="mt-3 space-y-1.5 list-disc pl-5 text-primary/85">
                <li>Répondre aux demandes de contact</li>
                <li>Établir des devis personnalisés</li>
                <li>Assurer le suivi client</li>
              </ul>
            </Section>

            <Section index="03" title="Conservation des données">
              <p>
                Les données sont conservées pendant une durée maximale de
                <strong> 3 ans</strong>.
              </p>
            </Section>

            <Section index="04" title="Partage des données">
              <p>
                Les données peuvent être transmises à des partenaires
                techniques dans le cadre de la réalisation des prestations
                (ex : bureau d'étude).
              </p>
              <p className="mt-3">
                <strong>Elles ne sont en aucun cas revendues.</strong>
              </p>
            </Section>

            <Section index="05" title="Sécurité">
              <p>
                EM GROUP met en œuvre les mesures nécessaires pour protéger
                les données personnelles.
              </p>
            </Section>

            <Section index="06" title="Vos droits">
              <p>Conformément au RGPD, vous pouvez :</p>
              <ul className="mt-3 space-y-1.5 list-disc pl-5 text-primary/85">
                <li>Demander l'accès à vos données</li>
                <li>Demander leur modification</li>
                <li>Demander leur suppression</li>
              </ul>
              <p className="mt-4">
                Pour cela, contactez-nous à{" "}
                <a
                  href="mailto:contact@solmishabitat.re"
                  className="text-[hsl(var(--brand-terra))] underline underline-offset-4 hover:no-underline"
                >
                  contact@solmishabitat.re
                </a>
                .
              </p>
            </Section>

            <Section index="07" title="Cookies">
              <p>
                Le site peut utiliser des cookies à des fins de mesure
                d'audience et d'amélioration de l'expérience utilisateur.
              </p>
            </Section>
          </div>

          <div className="mt-16 pt-10 border-t border-border flex flex-wrap items-center gap-4">
            <Link to="/" data-testid="pc-back-bottom" className="btn-primary">
              <ArrowLeft size={16} strokeWidth={1.8} />
              Retour à l'accueil
            </Link>
            <Link
              to="/mentions-legales"
              className="text-sm text-primary/85 hover:text-[hsl(var(--brand-terra))] underline underline-offset-4"
            >
              Voir les mentions légales
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

function Section({ index, title, children }) {
  return (
    <section>
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[hsl(var(--brand-terra))] mb-2">
        {index}
      </p>
      <h2 className="font-display text-2xl md:text-3xl text-primary leading-snug">
        {title}
      </h2>
      <span className="block h-0.5 w-10 bg-[hsl(var(--brand-terra))] mt-3 mb-5" />
      <div className="space-y-2">{children}</div>
    </section>
  );
}
